import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { useFormik } from "formik";
import * as Yup from "yup";
import { toast } from "react-toastify";
import api from "../services/api";
import PageSkeleton from "../components/ui/PageSkeleton";
import Input from "../components/ui/Input";
import Button from "../components/ui/Button";
import Card from "../components/ui/Card";
import {
  Business,
  Email,
  Phone,
  LocationOn,
  AccessTime,
  EventNote,
  Mail,
  Add,
  Delete,
  CheckCircle,
  Cancel,
} from "@mui/icons-material";

const validationSchema = Yup.object({
  companyName: Yup.string().required("Company name is required"),
  companyEmail: Yup.string()
    .email("Invalid email")
    .required("Company email is required"),
  companyPhone: Yup.string().matches(/^[0-9]{10}$/, "Phone must be 10 digits"),
  companyAddress: Yup.string(),
  workingHoursPerDay: Yup.number()
    .min(1)
    .max(24)
    .required("Working hours required"),
  leavePerYear: Yup.number().min(0).required("Leave per year required"),
  emailNotifications: Yup.boolean(),
  autoApproveLeave: Yup.boolean(),
});

export default function Settings() {
  const [loading, setLoading] = useState(true);
  const [leaveApprovalEmails, setLeaveApprovalEmails] = useState([
    "hrms@scalecapacity.com",
  ]);

  const formik = useFormik({
    initialValues: {
      companyName: "",
      companyEmail: "",
      companyPhone: "",
      companyAddress: "",
      workingHoursPerDay: 8,
      leavePerYear: 20,
      emailNotifications: true,
      autoApproveLeave: false,
    },
    validationSchema: validationSchema,
    onSubmit: async (values, { setSubmitting }) => {
      try {
        await api.put("/settings", {
          ...values,
          leaveApprovalEmails: leaveApprovalEmails.filter(
            (email) => email.trim() !== ""
          ),
        });
        toast.success("Settings updated successfully!");
      } catch (err) {
        toast.error(err.response?.data?.message || "Failed to update settings");
      } finally {
        setSubmitting(false);
      }
    },
  });

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const response = await api.get("/settings");
        const settings = response.data;

        formik.setValues({
          companyName: settings.companyName || "",
          companyEmail: settings.companyEmail || "",
          companyPhone: settings.companyPhone || "",
          companyAddress: settings.companyAddress || "",
          workingHoursPerDay: settings.workingHoursPerDay || 8,
          leavePerYear: settings.leavePerYear || 20,
          emailNotifications:
            settings.emailNotifications !== undefined
              ? settings.emailNotifications
              : true,
          autoApproveLeave:
            settings.autoApproveLeave !== undefined
              ? settings.autoApproveLeave
              : false,
        });

        setLeaveApprovalEmails(
          settings.leaveApprovalEmails &&
            settings.leaveApprovalEmails.length > 0
            ? settings.leaveApprovalEmails
            : ["hrms@scalecapacity.com"]
        );
      } catch (err) {
        console.error("Failed to fetch settings", err);
        toast.error("Failed to load settings");
      } finally {
        setLoading(false);
      }
    };

    fetchSettings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleAddEmail = () => {
    setLeaveApprovalEmails([...leaveApprovalEmails, ""]);
  };

  const handleRemoveEmail = (index) => {
    if (leaveApprovalEmails.length > 1) {
      const newEmails = leaveApprovalEmails.filter((_, i) => i !== index);
      setLeaveApprovalEmails(newEmails);
    }
  };

  const handleEmailChange = (index, value) => {
    const newEmails = [...leaveApprovalEmails];
    newEmails[index] = value;
    setLeaveApprovalEmails(newEmails);
  };

  const isValidEmail = (email) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto">
        <PageSkeleton type="form" />
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
      >
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-primary mb-2">Settings</h1>
          <p className="text-gray-600">
            Manage company settings and configurations
          </p>
        </div>

        <form onSubmit={formik.handleSubmit}>
          {/* Company Information */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
          >
            <Card className="mb-6">
              <h3 className="text-xl font-bold text-primary mb-6 pb-4 border-b border-gray-200">
                Company Information
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <Business className="w-5 h-5 text-gray-400" />
                    <label className="text-sm font-semibold text-gray-700">
                      Company Name
                    </label>
                  </div>
                  <Input
                    id="companyName"
                    name="companyName"
                    type="text"
                    value={formik.values.companyName}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={
                      formik.touched.companyName &&
                      Boolean(formik.errors.companyName)
                        ? formik.errors.companyName
                        : ""
                    }
                    placeholder="Enter company name"
                  />
                </div>

                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <Email className="w-5 h-5 text-gray-400" />
                    <label className="text-sm font-semibold text-gray-700">
                      Company Email
                    </label>
                  </div>
                  <Input
                    id="companyEmail"
                    name="companyEmail"
                    type="email"
                    value={formik.values.companyEmail}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={
                      formik.touched.companyEmail &&
                      Boolean(formik.errors.companyEmail)
                        ? formik.errors.companyEmail
                        : ""
                    }
                    placeholder="Enter company email"
                  />
                </div>

                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <Phone className="w-5 h-5 text-gray-400" />
                    <label className="text-sm font-semibold text-gray-700">
                      Company Phone
                    </label>
                  </div>
                  <Input
                    id="companyPhone"
                    name="companyPhone"
                    type="tel"
                    value={formik.values.companyPhone}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={
                      formik.touched.companyPhone &&
                      Boolean(formik.errors.companyPhone)
                        ? formik.errors.companyPhone
                        : ""
                    }
                    placeholder="Enter company phone"
                  />
                </div>

                <div className="md:col-span-2">
                  <div className="flex items-center gap-2 mb-2">
                    <LocationOn className="w-5 h-5 text-gray-400" />
                    <label className="text-sm font-semibold text-gray-700">
                      Company Address
                    </label>
                  </div>
                  <textarea
                    id="companyAddress"
                    name="companyAddress"
                    value={formik.values.companyAddress}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    rows={3}
                    className="input-field w-full resize-none"
                    placeholder="Enter company address"
                  />
                </div>
              </div>
            </Card>
          </motion.div>

          {/* Work Configuration */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
          >
            <Card className="mb-6">
              <h3 className="text-xl font-bold text-primary mb-6 pb-4 border-b border-gray-200">
                Work Configuration
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <AccessTime className="w-5 h-5 text-gray-400" />
                    <label className="text-sm font-semibold text-gray-700">
                      Working Hours Per Day
                    </label>
                  </div>
                  <Input
                    id="workingHoursPerDay"
                    name="workingHoursPerDay"
                    type="number"
                    value={formik.values.workingHoursPerDay}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={
                      formik.touched.workingHoursPerDay &&
                      Boolean(formik.errors.workingHoursPerDay)
                        ? formik.errors.workingHoursPerDay
                        : ""
                    }
                    placeholder="8"
                  />
                </div>

                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <EventNote className="w-5 h-5 text-gray-400" />
                    <label className="text-sm font-semibold text-gray-700">
                      Leave Days Per Year
                    </label>
                  </div>
                  <Input
                    id="leavePerYear"
                    name="leavePerYear"
                    type="number"
                    value={formik.values.leavePerYear}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={
                      formik.touched.leavePerYear &&
                      Boolean(formik.errors.leavePerYear)
                        ? formik.errors.leavePerYear
                        : ""
                    }
                    placeholder="20"
                  />
                </div>
              </div>
            </Card>
          </motion.div>

          {/* Email Notifications */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
          >
            <Card className="mb-6">
              <h3 className="text-xl font-bold text-primary mb-6 pb-4 border-b border-gray-200">
                Email Notifications
              </h3>
              <div className="space-y-4">
                <div className="flex items-center justify-between p-4 bg-gray-50 rounded-2xl">
                  <div className="flex items-center gap-3">
                    <Mail className="w-5 h-5 text-primary" />
                    <div>
                      <label className="text-sm font-semibold text-gray-700">
                        Enable Email Notifications
                      </label>
                      <p className="text-xs text-gray-500">
                        Send email notifications for important events
                      </p>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() =>
                      formik.setFieldValue(
                        "emailNotifications",
                        !formik.values.emailNotifications
                      )
                    }
                    className={`relative w-14 h-7 rounded-full transition-colors ${
                      formik.values.emailNotifications
                        ? "bg-primary"
                        : "bg-gray-300"
                    }`}
                  >
                    <span
                      className={`absolute top-1 left-1 w-5 h-5 bg-white rounded-full transition-transform ${
                        formik.values.emailNotifications
                          ? "translate-x-7"
                          : "translate-x-0"
                      }`}
                    />
                  </button>
                </div>

                <div className="flex items-center justify-between p-4 bg-gray-50 rounded-2xl">
                  <div className="flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-primary" />
                    <div>
                      <label className="text-sm font-semibold text-gray-700">
                        Auto Approve Leave Requests
                      </label>
                      <p className="text-xs text-gray-500">
                        Automatically approve leave requests without manual
                        review
                      </p>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() =>
                      formik.setFieldValue(
                        "autoApproveLeave",
                        !formik.values.autoApproveLeave
                      )
                    }
                    className={`relative w-14 h-7 rounded-full transition-colors ${
                      formik.values.autoApproveLeave
                        ? "bg-primary"
                        : "bg-gray-300"
                    }`}
                  >
                    <span
                      className={`absolute top-1 left-1 w-5 h-5 bg-white rounded-full transition-transform ${
                        formik.values.autoApproveLeave
                          ? "translate-x-7"
                          : "translate-x-0"
                      }`}
                    />
                  </button>
                </div>
              </div>
            </Card>
          </motion.div>

          {/* Leave Approval Emails */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
          >
            <Card className="mb-6">
              <h3 className="text-xl font-bold text-primary mb-6 pb-4 border-b border-gray-200">
                Leave Approval Email Recipients
              </h3>
              <p className="text-sm text-gray-600 mb-6">
                Configure email addresses that will receive leave approval
                notifications. At least one valid email is required.
              </p>
              <div className="space-y-4">
                {leaveApprovalEmails.map((email, index) => (
                  <div
                    key={index}
                    className="flex items-start gap-3 p-4 bg-gray-50 rounded-2xl"
                  >
                    <div className="flex-1">
                      <Input
                        type="email"
                        value={email}
                        onChange={(e) =>
                          handleEmailChange(index, e.target.value)
                        }
                        error={
                          email.trim() !== "" && !isValidEmail(email)
                            ? "Invalid email address"
                            : ""
                        }
                        placeholder={`Email ${index + 1}`}
                      />
                    </div>
                    <button
                      type="button"
                      onClick={() => handleRemoveEmail(index)}
                      disabled={leaveApprovalEmails.length === 1}
                      className="p-2 text-red-500 hover:bg-red-50 rounded-xl transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <Delete className="w-5 h-5" />
                    </button>
                  </div>
                ))}
                <Button
                  type="button"
                  variant="secondary"
                  onClick={handleAddEmail}
                  className="flex items-center gap-2"
                >
                  <Add className="w-5 h-5" />
                  Add More Email
                </Button>
              </div>
            </Card>
          </motion.div>

          {/* Submit Button */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="flex justify-end"
          >
            <Button
              type="submit"
              variant="primary"
              disabled={formik.isSubmitting}
              className="min-w-[200px]"
            >
              {formik.isSubmitting ? "Saving..." : "Save Settings"}
            </Button>
          </motion.div>
        </form>
      </motion.div>
    </div>
  );
}
